import { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { ticketsApi } from '@/shared/api/tickets';
import { apiErrorText } from '@/shared/api';
import {
  Field,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Textarea,
  toast,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import styles from './ResolutionDecisionDialog.module.css';

const COMMENT_REQUIRED_BELOW = 3;

export interface ResolutionDecisionDialogProps {
  ticketId: string;
  mode: 'confirm' | 'reject' | null;
  onClose: () => void;
  onDone: () => void;
}

const RATINGS = [1, 2, 3, 4, 5] as const;

const COMMENT_ID = 'resolution-decision-comment';

const RATING_WORDS: Record<number, string> = {
  1: 'Плохо',
  2: 'Ниже ожиданий',
  3: 'Нормально',
  4: 'Хорошо',
  5: 'Отлично',
};

export const ResolutionDecisionDialog: React.FC<ResolutionDecisionDialogProps> = ({
  ticketId,
  mode,
  onClose,
  onDone,
}) => {
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [commentTouched, setCommentTouched] = useState(false);

  useEffect(() => {
    if (mode) {
      setRating(null);
      setComment('');
      setCommentTouched(false);
    }
  }, [mode]);

  const trimmed = comment.trim();
  const commentRequired =
    mode === 'reject' || (rating != null && rating < COMMENT_REQUIRED_BELOW);
  const showCommentHint =
    commentRequired && !trimmed && (mode === 'reject' ? commentTouched : true);
  const canSubmit =
    !sending &&
    (mode === 'reject' ? trimmed.length > 0 : rating != null && (!commentRequired || trimmed.length > 0));

  const submit = async () => {
    if (!canSubmit || !mode) return;
    setSending(true);
    try {
      if (mode === 'confirm') {
        await ticketsApi.confirmResolution(ticketId, rating as number, trimmed || undefined);
        toast.success('Спасибо! Заявка закрыта');
      } else {
        await ticketsApi.rejectResolution(ticketId, trimmed);
        toast.success('Отправлено исполнителю');
      }
      onDone();
      onClose();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отправить решение'));
    } finally {
      setSending(false);
    }
  };

  const starRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const focusStar = (value: number) => {
    starRefs.current[value]?.focus();
  };

  const handleStarKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, value: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      next = Math.max(1, value - 1);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      next = Math.min(5, value + 1);
    } else if (e.key === 'Home') {
      next = 1;
    } else if (e.key === 'End') {
      next = 5;
    }
    if (next != null) {
      e.preventDefault();
      setRating(next);
      focusStar(next);
    }
  };

  const activeRating = rating ?? RATINGS[0];

  return (
    <Dialog
      open={mode != null}
      onOpenChange={(open) => !open && !sending && onClose()}
    >
      <DialogContent className={styles.popup}>
        <header className={styles.header}>
          <DialogTitle className={styles.title}>
            {mode === 'reject' ? 'Не подтверждаю' : 'Подтверждение выполнения'}
          </DialogTitle>
          <DialogDescription className={styles.description}>
            {mode === 'reject'
              ? 'Заявка вернётся исполнителю с вашим замечанием.'
              : 'Оцените, как выполнена работа.'}
          </DialogDescription>
        </header>

        {mode === 'confirm' && (
          <div className={styles.field}>
            { }
            <div className={styles.stars} role="radiogroup" aria-label="Оценка исполнения">
              {RATINGS.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  ref={(el) => {
                    starRefs.current[value] = el;
                  }}
                  aria-checked={rating === value}
                  aria-label={`${value} — ${RATING_WORDS[value]}`}
                  tabIndex={value === activeRating ? 0 : -1}
                  className={cn(
                    styles.star,
                    rating != null && value <= rating && styles.starActive,
                  )}
                  onClick={() => setRating(value)}
                  onKeyDown={(e) => handleStarKeyDown(e, value)}
                >
                  <Star size={22} aria-hidden />
                </button>
              ))}
            </div>
            <span className={styles.ratingWord} aria-hidden>
              {rating != null ? RATING_WORDS[rating] : 'Оценка не выбрана'}
            </span>
          </div>
        )}

        <div className={styles.field}>
          <Field
            label={mode === 'reject' ? 'Что не сделано' : 'Комментарий'}
            required={commentRequired}
            hint={commentRequired ? undefined : 'По желанию'}
          >
            <Textarea
              id={COMMENT_ID}
              invalid={showCommentHint}
              rows={3}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setCommentTouched(true);
              }}
              onBlur={() => setCommentTouched(true)}
              maxLength={2000}
              aria-describedby={showCommentHint ? `${COMMENT_ID}-error` : undefined}
            />
          </Field>
          {showCommentHint && (
            <p className={styles.error} id={`${COMMENT_ID}-error`} role="alert">
              {mode === 'reject'
                ? 'Опишите, что доделать — иначе исполнитель не поймёт замечание.'
                : 'При оценке ниже 3 комментарий обязателен.'}
            </p>
          )}
        </div>

        <DialogFooter className={styles.footer}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={sending}>
            Отмена
          </Button>
          <Button
            variant={mode === 'reject' ? 'destructive' : 'primary'}
            size="sm"
            onClick={submit}
            disabled={!canSubmit}
          >
            {sending ? 'Отправка…' : mode === 'reject' ? 'Отправить' : 'Подтвердить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
